import * as React from 'react';
import { cn } from '@common/bw-ui/lib/utils';
import { Button } from '@common/bw-ui/ui/button';
import { Alert } from '@common/bw-ui/ui/alert';
import { ArrowRightIcon, InfoIcon, LoaderIcon } from '@common/bw-ui/icons';

export interface DiscountLine {
  label: string;
  amount: number;
  type?: 'discount' | 'credit';
}

export interface BookingSummaryProps {
  subtotal: number;
  tax: number;
  /** Gross, VAT-inclusive total after booking credits, before account credits. */
  total: number;
  /** Account credit expected to be applied — hidden when £0. */
  accountCredits?: number;
  /** What the customer will actually pay. Defaults to `total`. */
  amountDue?: number;
  discountLines?: DiscountLine[];
  error?: string;
  onProceed: () => void;
  onProceedLoading: boolean;
  children?: React.ReactNode;
  className?: string;
  cancellationPolicyMinutes?: number;
  canCancelBooking?: boolean;
}

function formatPrice(price: number): string {
  return `£${price.toFixed(2)}`;
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:justify-between tw:items-center">
      <span className="tw:text-sm tw:text-muted">{label}</span>
      <span className="tw:text-sm tw:text-foreground">{value}</span>
    </div>
  );
}

function DiscountRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:justify-between tw:items-center">
      <span className="tw:text-sm tw:text-success">{label}</span>
      <span className="tw:text-sm tw:text-success">{value}</span>
    </div>
  );
}

function CreditRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:justify-between tw:items-center">
      <span className="tw:text-sm tw:text-info">{label}</span>
      <span className="tw:text-sm tw:text-info">{value}</span>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:justify-between tw:items-center">
      <span className="tw:text-lg tw:text-foreground">{label}</span>
      <span className="tw:text-2xl tw:font-bold tw:text-foreground">
        {value}
      </span>
    </div>
  );
}

function CancellationNotice({
  cancellationPolicyMinutes,
  canCancelBooking,
}: {
  cancellationPolicyMinutes?: number;
  canCancelBooking?: boolean;
}) {
  const cancellationPolicyHours = (cancellationPolicyMinutes ?? 0) / 60;

  // If cancellationPolicyMinutes is 0, it means there is no cancellation policy, so we don't show the notice
  if (cancellationPolicyMinutes === 0) return null;

  return (
    <div
      data-slot="cancellation"
      className="tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-info/30 tw:bg-info-light tw:p-4"
    >
      <InfoIcon
        className="tw:w-5 tw:h-5 tw:text-info tw:shrink-0 tw:mt-0.5"
        aria-hidden="true"
      />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <span className="tw:text-sm tw:font-medium tw:text-info-dark">
          Cancellation policy
        </span>
        <span className="tw:text-xs tw:text-info">
          {canCancelBooking
            ? `Cancel up to ${cancellationPolicyHours} hours before your booking for a full refund.`
            : `Booking starts in less than ${cancellationPolicyHours} hours, it is non-refundable and cannot be cancelled or changed once confirmed.`}
        </span>
      </div>
    </div>
  );
}

export function BookingSummary({
  subtotal,
  tax,
  total,
  accountCredits = 0,
  amountDue,
  discountLines,
  error,
  onProceed,
  onProceedLoading,
  children,
  className,
  cancellationPolicyMinutes,
  canCancelBooking,
}: BookingSummaryProps) {
  // Display order (BCD-1647): sub-total → discounts → booking credits → VAT →
  // Total (inc. VAT) → account credits → Amount due. Account credits settle the
  // gross total, so they sit below the VAT and Total lines, never above them.
  const payable = amountDue ?? total;
  const showAccountCredits = accountCredits > 0.005;

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col tw:gap-5 tw:rounded-lg tw:bg-white tw:p-6 tw:shadow-md',
        className,
      )}
    >
      <h2
        data-slot="heading"
        className="tw:text-xl tw:font-medium tw:text-foreground"
      >
        Summary
      </h2>

      {children}

      <div className="tw:h-px tw:bg-black/10" />
      {error ? (
        <Alert
          data-slot="error"
          variant="error"
          title="Something went wrong"
          description={error}
        />
      ) : (
        <>
          <div data-slot="totals" className="tw:flex tw:flex-col tw:gap-3">
            <SummaryRow label="Sub-total" value={formatPrice(subtotal)} />

            {discountLines && discountLines.length > 0 && (
              <div
                data-slot="discounts"
                className="tw:flex tw:flex-col tw:gap-2"
              >
                {discountLines.map((line, index) => {
                  const Row = line.type === 'credit' ? CreditRow : DiscountRow;
                  return (
                    <Row
                      key={`${line.type}-${line.label}-${index}`}
                      label={line.label}
                      value={
                        line.amount > 0 ? `-${formatPrice(line.amount)}` : ''
                      }
                    />
                  );
                })}
              </div>
            )}

            <div className="tw:h-px tw:bg-black/10 tw:my-1" />
            <SummaryRow label="VAT" value={formatPrice(tax)} />
            <SummaryRow label="Total" value={formatPrice(total)} />

            {showAccountCredits && (
              <CreditRow
                label="Account credits"
                value={`-${formatPrice(accountCredits)}`}
              />
            )}

            <TotalRow label="Amount due" value={formatPrice(payable)} />
          </div>

          <Button
            data-slot="proceed"
            variant="success"
            onClick={onProceed}
            // A second click would create a second invoice (and booking).
            disabled={onProceedLoading}
            className="tw:w-full"
          >
            {onProceedLoading ? (
              <>
                Loading...
                <LoaderIcon
                  className="tw:w-4 tw:h-4 tw:animate-spin"
                  aria-hidden="true"
                />
              </>
            ) : (
              <>
                {payable === 0 ? 'Continue' : 'Proceed to checkout'}
                <ArrowRightIcon className="tw:size-4" aria-hidden="true" />
              </>
            )}
          </Button>
          <CancellationNotice
            cancellationPolicyMinutes={cancellationPolicyMinutes}
            canCancelBooking={canCancelBooking}
          />
        </>
      )}
    </div>
  );
}
